import React, { useEffect } from "react";
import AttachedDocuments from "../AttachedDocuments";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { UseMutationResult } from "react-query";
import { useGetItemDocuments } from "../../api/360FilesAPI";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../../utils";

export interface DiligenceItem {
  external_id: string;
  name: string;
  source: string;
  date?: string | null;
  body?: string | null;
}

const diligenceFields = [
  {
    id: "name",
    label: "Name on Record",
    type: "text",
    placeholder: "Enter name on record",
    required: true,
  },
  {
    id: "source",
    label: "Source",
    type: "text",
    placeholder: "Enter source",
    required: true,
  },
  {
    id: "date",
    label: "Date",
    type: "date",
    placeholder: "",
    required: false,
  },
  {
    id: "body",
    label: "Body",
    type: "textarea",
    placeholder: "Enter body",
    required: false,
  },
  {
    id: "files",
    label: "File Attachment",
    type: "file",
    placeholder: "",
  },
];

const DiligenceRenderer: React.FC<{ doc: DiligenceItem }> = ({ doc }) => {
  const { data: uploadedFiles = [], refetch: refetchUploadedFiles } =
    useGetItemDocuments(doc.external_id, true);

  useEffect(() => {
    refetchUploadedFiles();
  }, [refetchUploadedFiles, doc.external_id]);

  return (
    <div className="min-w-0">
      <h3
        className={classNames(
          "text-gray-700 font-medium text-sm",
          LONG_TEXT_WRAP_CLASSES
        )}
      >
        {doc.name}
      </h3>
      {uploadedFiles.map((file: { filename: string; url: string }) => (
        <div
          key={file.filename}
          className="flex flex-row gap-2 p-1 cursor-pointer items-center min-w-0"
          onClick={() => window.open(file.url, "_blank")}
        >
          <span className="w-4 h-4 flex-shrink-0">
            <PaperClipIcon className="w-4 h-4" />
          </span>
          <p
            className={classNames(
              "text-gray-500 text-xs flex-1",
              LONG_TEXT_WRAP_CLASSES
            )}
          >
            {file.filename}
          </p>
        </div>
      ))}
      <p className="text-gray-500 text-xs">{doc.source}</p>
      {doc.date && <p className="text-gray-500 text-xs">{doc.date}</p>}
    </div>
  );
};

const Diligence: React.FC<{
  report_uuid: string;
  workflow_run_uuid: string;
}> = ({ report_uuid, workflow_run_uuid }) => {
  const {
    data: diligence,
    isLoading,
    error,
    updateNoInvalidate,
    remove,
    createNoInvalidate,
    invalidateQuery,
  } = useCompoundResourceApi<DiligenceItem>(
    `itemdal/reports/${report_uuid}/diligence`,
    `itemdal/diligence`
  );

  return (
    <AttachedDocuments
      title="Diligence"
      fields={diligenceFields}
      items={diligence}
      isLoading={isLoading}
      error={error as string}
      createItemMutation={
        createNoInvalidate as UseMutationResult<any, Error, any>
      }
      updateItemMutation={
        updateNoInvalidate as UseMutationResult<any, Error, any>
      }
      removeItemMutation={remove as UseMutationResult<any, Error, any>}
      invalidateQuery={invalidateQuery}
      confirmRemove={true}
      bgColorClass="bg-pink-500"
      DocRenderer={DiligenceRenderer}
      report_uuid={report_uuid}
      workflow_run_uuid={workflow_run_uuid}
      sortBy="date"
    />
  );
};

export default Diligence;
